import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { Mail, Loader2 } from "lucide-react";
import { useEnrichAllFlights } from "@/hooks/useFlightEnrichment";
import { useEnrichAllHotels } from "@/hooks/useHotelEnrichment";

interface ImportEmailDialogProps {
  trigger?: React.ReactNode;
}

export function ImportEmailDialog({ trigger }: ImportEmailDialogProps) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const enrichFlights = useEnrichAllFlights();
  const enrichHotels = useEnrichAllHotels();

  const handleImport = async () => {
    if (!emailContent.trim()) {
      toast({
        title: "Email content required",
        description: "Please paste the email content to import",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("parse-email-content", {
        body: {
          subject: subject.trim() || "Imported Email",
          content: emailContent.trim(),
        },
      });

      if (error) throw error;

      if (data.success && data.tripId) {
        toast({
          title: data.merged
            ? "Items merged into existing trip"
            : "Trip imported successfully!",
          description: data.merged
            ? `Merged into "${data.mergedTripName || data.tripData?.displayName || "your trip"}"`
            : data.tripData?.displayName || "Your trip has been created",
        });
        queryClient.invalidateQueries({ queryKey: ["trips"] });

        // Trigger price enrichment in background for imported items
        const tripData = data.tripData;
        if (tripData) {
          if (tripData.flights && tripData.flights.length > 0) {
            enrichFlights.mutate({ tripId: data.tripId, flights: tripData.flights });
          }
          if (tripData.hotels && tripData.hotels.length > 0) {
            enrichHotels.mutate({ tripId: data.tripId, hotels: tripData.hotels });
          }
        }

        setOpen(false);
        setSubject("");
        setEmailContent("");
      } else {
        toast({
          title: "Could not parse email",
          description: data.error || "Unable to extract trip details from the content",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error importing email:", error);
      toast({
        title: "Import failed",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Mail className="h-4 w-4" />
            Import Email
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Import from Email</DialogTitle>
          <DialogDescription>
            Paste the contents of a booking confirmation email to automatically extract trip details.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="subject">Email Subject (optional)</Label>
            <Input
              id="subject"
              placeholder="e.g., Your Flight Confirmation - AA1234"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="content">Email Content</Label>
            <Textarea
              id="content"
              placeholder="Paste the full email content here..."
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
              className="min-h-[250px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Tip: Copy the entire email including headers, dates, and confirmation numbers for best results.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleImport} disabled={isLoading || !emailContent.trim()}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                "Import Trip"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
